/** Barre latérale : revenus, recherche, filtres, paramètres. */

import { counts as staticCounts, idScheme as dataScheme } from '../data/establishments.js'
import { DEFAULT_RATIO_PRO, STATUSES } from '../domain/constants.js'
import { toggleStatus, toggleType } from '../domain/filters.js'
import { commercialEntities } from '../domain/entities.js'
import { computeRevenue, computeTotalEarned, countByStatus } from '../domain/revenue.js'
import { getState, setListOpen, setSettings, setStatusFilters, setTypeFilters, select } from '../state.js'
import { signOut } from '../supabase/auth.js'
import { saveSettings } from '../supabase/records.js'
import { $, $$, money, plural, setText } from './dom.js'
import { withToast } from './toast.js'

export function initSidebar({ flyTo }) {
  setText('count', staticCounts.gmf)
  setText('counth', staticCounts.hosp)

  buildSearchIndex(flyTo)
  wireFilters()
  wireSettings()
  wireExport()

  renderRevenue()
  renderCounts()
  renderFilters()
  renderSettings()
}

// --- Revenus ---------------------------------------------------------------

function renderRevenue() {
  const state = getState()
  const rev = computeRevenue(commercialEntities(state.establishments, state.records), state.records, state.settings)
  const tarif = Number.parseFloat(state.settings.tarif) || 0

  setText('revMonthly', money(rev.monthly))
  setText(
    'revSub',
    `${rev.clientCount} ${plural(rev.clientCount, 'client')} · ` +
      `${rev.userCount} ${plural(rev.userCount, 'utilisateur')}`,
  )
  setText(
    'revPipe',
    tarif > 0
      ? `Potentiel en négociation : ${money(rev.pipeline)}`
      : 'Définis le tarif par utilisateur (⚙) pour voir les revenus.',
  )

  renderEarned()
}

/**
 * Cumul facturé depuis les débuts.
 *
 * Repose sur l'effectif et le tarif d'aujourd'hui appliqués à toute la période
 * écoulée — faute d'historique des changements, c'est le mieux disponible. Les
 * clients sans date de début sont écartés du calcul et signalés : mieux vaut un
 * total incomplet mais dit que complet et faux.
 */
function renderEarned() {
  const state = getState()
  const { total, counted, skipped } = computeTotalEarned(
    commercialEntities(state.establishments, state.records),
    state.records,
    state.settings,
  )

  setText('revEarned', money(total))

  if (counted === 0 && skipped === 0) {
    setText('revEarnedSub', 'aucun client payant pour le moment')
    return
  }

  const base = `sur ${counted} ${plural(counted, 'client')}`
  setText(
    'revEarnedSub',
    skipped > 0
      ? `${base} · ${skipped} sans date de début, ${plural(skipped, 'exclu')} du total`
      : base,
  )
}

function renderCounts() {
  const state = getState()
  const counts = countByStatus(commercialEntities(state.establishments, state.records), state.records)
  for (const status of STATUSES) setText(`c-${status}`, counts[status])
}

// --- Filtres ---------------------------------------------------------------

/**
 * Un clic nu ISOLE ; Cmd/Ctrl-clic bascule.
 *
 * L'inverse de la convention Finder, et c'est délibéré : on vient ici pour
 * demander « montre-moi les clients », presque jamais pour retirer un statut
 * d'un ensemble. Le geste le plus fréquent mérite le geste le plus simple.
 */
const isolates = (e) => !(e.metaKey || e.ctrlKey)

function wireFilters() {
  for (const chip of $$('#chips .srow')) {
    chip.addEventListener('click', (e) => {
      setStatusFilters(toggleStatus(getState().statusFilters, chip.dataset.s, isolates(e)))
    })
  }

  for (const chip of $$('#types .trow')) {
    chip.addEventListener('click', (e) => {
      setTypeFilters(toggleType(getState().typeFilters, chip.dataset.t, isolates(e)))
    })
  }

  // Le compteur ouvre la liste de son statut, et la referme au second clic.
  // stopPropagation : sans lui, le clic remonterait à la ligne et jouerait
  // aussi le filtre — deux actions pour un geste.
  for (const count of $$('#chips .n')) {
    count.addEventListener('click', (e) => {
      e.stopPropagation()
      const status = count.dataset.n
      const state = getState()

      if (state.listOpen && isOnly(state.statusFilters, status)) {
        setListOpen(false)
        return
      }
      // Isoler le statut : le chiffre annonce « 2 clients », la liste doit
      // montrer ces 2 — pas les prospects qui étaient filtrés jusque-là.
      setStatusFilters(toggleStatus(state.statusFilters, status, true))
      setListOpen(true)
    })
  }

  // Le « × » de la colonne ne fait plus que la fermer : il réinitialisait aussi
  // les filtres, ce qui, maintenant que la colonne est commandée à la main,
  // reviendrait à défaire un réglage qu'on n'a pas touché. Voir ui/list.js.
}

/** true si `status` est le seul statut affiché. */
function isOnly(filters, status) {
  return STATUSES.every((s) => filters[s] === (s === status))
}

function renderFilters() {
  const state = getState()
  for (const chip of $$('#chips .srow')) {
    chip.classList.toggle('off', !state.statusFilters[chip.dataset.s])
  }
  for (const chip of $$('#types .trow')) {
    chip.classList.toggle('off', !state.typeFilters[chip.dataset.t])
  }
  for (const count of $$('#chips .n')) {
    count.classList.toggle('on', state.listOpen && isOnly(state.statusFilters, count.dataset.n))
  }
}

// --- Recherche -------------------------------------------------------------

/**
 * Le datalist natif fait tout le travail de filtrage. L'index rattache le
 * libellé choisi à son établissement — la municipalité désambiguïse les
 * homonymes, fréquents chez les GMF multi-sites.
 */
function buildSearchIndex(flyTo) {
  const state = getState()
  const datalist = $('estlist')
  const byLabel = new Map()
  const fragment = document.createDocumentFragment()

  for (const est of state.establishments) {
    const suffix = est.kind === 'hosp' ? ' (hôpital)' : ''
    const label = `${est.nom}${suffix} — ${est.mun}`
    byLabel.set(label.toLowerCase(), est)
    const option = document.createElement('option')
    option.value = label
    fragment.appendChild(option)
  }
  datalist.appendChild(fragment)

  $('search').addEventListener('change', (e) => {
    const est = byLabel.get(e.target.value.toLowerCase())
    if (!est) return
    flyTo(est, 14)
    select(est.kind, est.id)
  })
}

// --- Paramètres ------------------------------------------------------------

function wireSettings() {
  $('btnSettings').addEventListener('click', () => {
    $('settingsPanel').classList.toggle('open')
  })

  $('tarif').addEventListener('change', async (e) => {
    const next = { ...getState().settings, tarif: Number.parseFloat(e.target.value) || 0 }
    setSettings(next) // optimiste : l'affichage ne doit pas attendre le réseau
    await withToast(saveSettings(next), { failure: 'Tarif non enregistré.' })
  })

  $('ratio').addEventListener('change', async (e) => {
    const value = Number.parseFloat(e.target.value) || 0
    const ratioPro = value > 0 ? value : DEFAULT_RATIO_PRO
    e.target.value = ratioPro // une valeur refusée doit se voir corrigée dans le champ
    const next = { ...getState().settings, ratioPro }
    setSettings(next)
    await withToast(saveSettings(next), { failure: 'Ratio non enregistré.' })
  })
}

function renderSettings() {
  const { settings, user } = getState()
  const tarif = $('tarif')
  const ratio = $('ratio')

  // Ne pas écrire dans un champ en cours de saisie : le curseur sauterait.
  if (document.activeElement !== tarif) tarif.value = settings.tarif || ''
  if (document.activeElement !== ratio) ratio.value = settings.ratioPro || DEFAULT_RATIO_PRO

  if (user) {
    $('signoutRow').innerHTML = ''
    $('signoutRow').append(
      document.createTextNode(`Connecté : ${user.email} · `),
      Object.assign(document.createElement('span'), {
        className: 'lk',
        textContent: 'se déconnecter',
        onclick: () => signOut(),
      }),
    )
  }
}

// --- Export ----------------------------------------------------------------

/**
 * Sauvegarde manuelle. Les données sont désormais dans Postgres, sauvegardé par
 * Supabase : l'export n'est plus le filet de sécurité qu'il était, mais il
 * reste le moyen de sortir les données de l'outil — vers un tableur, ou
 * ailleurs.
 */
function wireExport() {
  $('btnExport').addEventListener('click', () => {
    const state = getState()
    const payload = {
      exportedAt: new Date().toISOString(),
      exportedBy: state.user?.email || null,
      idScheme: dataScheme, // pour qu'un export daté reste interprétable
      settings: state.settings,
      records: [...state.records.values()],
      notes: [...state.notes.values()].flat(),
    }

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const stamp = new Date().toISOString().slice(0, 10)
    link.href = url
    link.download = `gmf-crm-quebec-${stamp}.json`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1500)
  })
}

/** Redessine seulement ce que le changement concerne. */
export function updateSidebar(changed) {
  if (changed.includes('records') || changed.includes('settings') || changed.includes('notes')) {
    renderRevenue()
    renderCounts()
  }
  if (changed.includes('settings') || changed.includes('user')) renderSettings()
  // listOpen : c'est lui qui allume le compteur du statut affiché.
  if (['statusFilters', 'typeFilters', 'listOpen'].some((c) => changed.includes(c))) renderFilters()
}
